import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Search, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import ReviewItem from './review-item';
import reviewsViewModel from '../../view-model/professional/reviews-view-model';
import { Review } from '../../model/professional/reviews-model';
import { useTranslation } from 'react-i18next';

interface ReviewListProps {
  isLoading?: boolean;
}

const ReviewList: React.FC<ReviewListProps> = observer(({ isLoading = false }) => {
  const { t } = useTranslation();
  const [filterType, setFilterType] = useState<'all' | 'with-response' | 'without-response'>('all');
  
  // Filtrer les avis selon le type de filtre
  const getFilteredReviews = (): Review[] => {
    const reviews = reviewsViewModel.filteredReviews;
    
    switch (filterType) {
      case 'with-response':
        return reviews.filter(review => review.response && review.response.trim() !== '');
      case 'without-response':
        return reviews.filter(review => !review.response || review.response.trim() === '');
      default:
        return reviews;
    }
  };
  
  const filteredReviews = getFilteredReviews();
  const totalReviews = reviewsViewModel.filteredReviewsBeforePagination.length;
  const totalPages = reviewsViewModel.totalPages;
  const currentPage = reviewsViewModel.currentPageNumber;

  // Fonction pour changer de page
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    reviewsViewModel.setCurrentPage(page);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 animate-pulse">
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2"></div>
                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-2"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-40"></div>
              </div>
            </div>
            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative w-full sm:w-64">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={16} className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder={t("reviews.search") || ""}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white text-sm"
            value={reviewsViewModel.searchTerm}
            onChange={(e) => {
              reviewsViewModel.filterReviews(e.target.value);
              reviewsViewModel.setCurrentPage(1); // Revenir à la première page lors d'une recherche
            }}
          />
        </div>
        
        <div className="flex items-center space-x-2 w-full sm:w-auto">
          <Filter size={16} className="text-gray-500 dark:text-gray-400" />
          <select
            className="border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white text-sm py-2 px-3"
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value as any);
              reviewsViewModel.setCurrentPage(1); // Revenir à la première page lors d'un changement de filtre
            }}
          >
            <option value="all">{t("reviews.filter.all")}</option>
            <option value="with-response">{t("reviews.filter.withResponse")}</option>
            <option value="without-response">{t("reviews.filter.withoutResponse")}</option>
          </select>
        </div>
      </div>

      {totalReviews === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            {reviewsViewModel.searchTerm || filterType !== 'all'
              ? t("reviews.noReviewsFound")
              : t("reviews.noReviews")}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {filteredReviews.map((review) => (
              <ReviewItem key={review.id} review={review} />
            ))}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center mt-6 space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-1 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={t("reviews.previous") || ""}
              >
                <ChevronLeft size={20} />
              </button>
              
              <div className="flex space-x-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`w-8 h-8 flex items-center justify-center rounded-md text-sm ${page === currentPage
                      ? 'bg-primary text-white'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                  >
                    {page}
                  </button>
                ))}
              </div>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-1 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={t("reviews.next") || ""}
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
          
          <div className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">
            {t("reviews.displaying")} {Math.min((currentPage - 1) * reviewsViewModel.itemsPerPageCount + 1, totalReviews)}-{Math.min(currentPage * reviewsViewModel.itemsPerPageCount, totalReviews)} {t("reviews.of")} {totalReviews} {t("reviews.reviews")}
          </div>
        </>
      )}
    </div>
  );
});

export default ReviewList;
